import React from 'react';
import { FaPlus, FaBell, FaCheck } from 'react-icons/fa';
import { ActionInputProps } from '../../types';
import { Select } from '../../../../common/Select';
import { Input } from '../../../../common/Input';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fr } from 'date-fns/locale';
import { useAppContext } from '../../../../../context/AppContext';
import {
  ActionInputContainer,
  ActionSecondLineContainer,
  ActionTypeContainer,
  AddTypeButton,
  SaveActionButton,
  ActionReminderContainer,
  DatePickerContainer,
  DatePickerButtonContainer
} from '../../styles';

/**
 * Composant pour l'ajout d'une nouvelle action
 */
const ActionInput: React.FC<ActionInputProps> = ({
  newActionType,
  setNewActionType,
  newActionDetails,
  setNewActionDetails,
  newCustomType,
  setNewCustomType,
  showNewTypeInput,
  setShowNewTypeInput,
  reminderDate,
  setReminderDate,
  tempReminderDate,
  setTempReminderDate,
  showDatePicker,
  setShowDatePicker,
  onAddAction,
  customActionTypes,
  isReadOnly = false
}) => {
  const { dispatch } = useAppContext();
  
  // Si en mode lecture seule, ne rien afficher
  if (isReadOnly) {
    return null;
  }
  console.log('🔍 DEBUG: ActionInput render', {
    newActionType,
    showNewTypeInput,
    newCustomType,
    timestamp: new Date().toISOString()
  });

  // Fonction pour obtenir la date par défaut (aujourd'hui à 9h00)
  const getDefaultReminderDate = () => {
    const defaultDate = new Date();
    defaultDate.setHours(9, 0, 0, 0);
    return defaultDate;
  };

  // Fonction pour gérer la sélection de la date de rappel
  const handleDateConfirm = () => {
    setReminderDate(tempReminderDate);
    setShowDatePicker(false);
  };

  // Fonction pour annuler la sélection de la date de rappel
  const handleDateCancel = () => {
    setShowDatePicker(false);
  };

  return (
    <>
      <ActionInputContainer>
        {/* Première ligne : Type d'action + bouton + */}
        <ActionTypeContainer>
          {showNewTypeInput ? (
            <Input
              type="text"
              value={newCustomType}
              onChange={(e) => setNewCustomType(e.target.value)}
              placeholder="Nouveau type..."
              style={{ width: '100%', fontSize: '0.75rem' }}
            />
          ) : (
            <Select
              value={newActionType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                console.log('🔍 DEBUG: Changement de sélection:', e.target.value);
                setNewActionType(e.target.value);
              }}
              style={{ width: '100%', fontSize: '0.75rem' }}
              options={(() => {
                // Actions par défaut
                const defaultActions = [
                  { value: 'appeler', label: 'Appeler' },
                  { value: 'rendre_visite', label: 'Rendre visite' },
                  { value: 'deposer_courrier', label: 'Déposer courrier' }
                ];
                
                // Actions personnalisées existantes (exclure les doublons avec actions par défaut)
                const defaultValues = new Set(defaultActions.map(a => a.value));
                const customActions = customActionTypes
                  .filter(type => !defaultValues.has(type))
                  .map(type => ({ 
                    value: type, 
                    label: type.charAt(0).toUpperCase() + type.slice(1) 
                  }));
                
                // Action personnalisée en cours de création
                const currentCustomAction = (newCustomType && !showNewTypeInput && !defaultValues.has(newActionType)) ? [{ 
                  value: newActionType, 
                  label: newCustomType.charAt(0).toUpperCase() + newCustomType.slice(1) 
                }] : [];
                
                // Fusionner toutes les actions et trier par ordre alphabétique
                const allActions = [...defaultActions, ...customActions, ...currentCustomAction]
                  .sort((a, b) => a.label.localeCompare(b.label));
                
                return [
                  { value: '', label: 'Sélectionner...' },
                  ...allActions
                ];
              })()}
            >
            </Select>
          )}
          <AddTypeButton
            type="button"
            onClick={(e) => {
              console.log('🔍 DEBUG: Clic sur bouton + personnalisé', {
                showNewTypeInput,
                newActionType,
                newCustomType,
                timestamp: new Date().toISOString()
              });
              
              e.preventDefault();
              e.stopPropagation();
              
              if (showNewTypeInput) {
                console.log('🔍 DEBUG: Mode validation - newCustomType:', newCustomType);
                if (newCustomType.trim()) {
                  console.log('🔍 DEBUG: Validation du nouveau type:', newCustomType.trim());
                  const newType = newCustomType.trim();
                  
                  // Ajouter le nouveau type à la liste globale des types d'actions personnalisés
                  dispatch({ type: 'ADD_CUSTOM_ACTION_TYPE', payload: newType });
                  
                  // Définir ce nouveau type comme sélectionné
                  setNewActionType(newType);
                  setShowNewTypeInput(false);
                  setNewCustomType(''); // Réinitialiser le champ de saisie
                  
                  console.log('🔍 DEBUG: Nouveau type ajouté au state global:', newType);
                } else {
                  console.log('🔍 DEBUG: Nouveau type vide, pas de validation');
                }
              } else {
                console.log('🔍 DEBUG: Mode création - newActionType:', newActionType);
                if (newActionType === 'custom') {
                  console.log('🔍 DEBUG: Activation du mode saisie personnalisé');
                  setShowNewTypeInput(true);
                  setNewActionType('');
                } else {
                  console.log('🔍 DEBUG: Forcer le mode personnalisé même sans sélection "custom"');
                  // Forcer l'activation du mode personnalisé
                  setShowNewTypeInput(true);
                  setNewActionType('');
                }
              }
            }}
            $isCheck={showNewTypeInput}
          >
            {showNewTypeInput ? <FaCheck /> : <FaPlus />}
          </AddTypeButton>
        </ActionTypeContainer>

        {/* Deuxième ligne : Description + cloche + bouton sauvegarde */}
        <ActionSecondLineContainer>
          <Input
            type="text"
            value={newActionDetails}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewActionDetails(e.target.value)}
            placeholder="Description de l'action..."
            style={{ flex: 1, fontSize: '0.75rem' }}
          />

          <ActionReminderContainer
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDatePicker(true);
            }}
            title={reminderDate ? `Rappel: ${reminderDate.toLocaleDateString()} ${reminderDate.toLocaleTimeString()}` : 'Ajouter un rappel'}
          >
            <FaBell style={{ color: reminderDate ? '#ff9800' : '#ffc107', fontSize: '18px' }} />
          </ActionReminderContainer>

          <SaveActionButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddAction();
            }}
            disabled={!newActionType || !newActionDetails}
          >
            <FaPlus />
          </SaveActionButton>
        </ActionSecondLineContainer>
      </ActionInputContainer>

      {showDatePicker && (
        <DatePickerContainer>
          <DatePicker
            selected={tempReminderDate || getDefaultReminderDate()}
            onChange={(date) => setTempReminderDate(date as Date)}
            inline
            locale={fr}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            timeCaption="Heure"
            minDate={new Date()}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
          />
          <DatePickerButtonContainer>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDateCancel(); }}>Annuler</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDateConfirm(); }}>Confirmer</button>
          </DatePickerButtonContainer>
        </DatePickerContainer>
      )}
    </>
  );
};

export default ActionInput;

